import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../../features/productSlice";
import ProductList from "./list/ProductList";
import API from "../../../util/API";
import { AlertTriangle, Loader2 } from "lucide-react";

const normalizeCountryCode = (value, fallback = "") => {
  const code = String(value || "").trim().toUpperCase();
  return code || fallback;
};

const normalizeCurrencyCode = (value, fallback = "") => {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : fallback;
};

const uniqueCurrencyCodes = (...values) => {
  const out = [];
  values.flat().forEach((value) => {
    const code = normalizeCurrencyCode(value);
    if (code && !out.includes(code)) out.push(code);
  });
  return out;
};

const buildCurrencyMeta = (country) => {
  const allowed = Array.isArray(country?.allowedCurrencyCodes) ? country.allowedCurrencyCodes : [];
  const preferred = normalizeCurrencyCode(country?.defaultCurrencyCode || country?.currencyCode);
  const options = uniqueCurrencyCodes(preferred, country?.currencyCode, allowed);
  const currencyOptions = options.length ? options : ["USD"];
  return {
    preferredCurrency: currencyOptions.includes(preferred) ? preferred : currencyOptions[0],
    currencyOptions,
  };
};

const getPreferredCurrency = (meta) =>
  normalizeCurrencyCode(meta?.preferredCurrency || meta?.currencyOptions?.[0], "USD");

export default function ProductManagement() {
  const dispatch = useDispatch();
  const { products, status, error, pagination } = useSelector((state) => state.products);

  const settingsCountry = useSelector((state) => state.userSettings?.shipToCountry) || "BD";
  const defaultCountry = normalizeCountryCode(settingsCountry, "BD");

  const [country, setCountry] = useState(defaultCountry);
  const [currency, setCurrency] = useState("USD");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [enabledCountries, setEnabledCountries] = useState([defaultCountry]);
  const [enabledCountryNames, setEnabledCountryNames] = useState({ [defaultCountry]: defaultCountry });
  const [countryCurrencyMeta, setCountryCurrencyMeta] = useState({
    [defaultCountry]: { preferredCurrency: "USD", currencyOptions: ["USD"] },
  });

  useEffect(() => {
    let cancelled = false;

    const loadCountries = async () => {
      try {
        const res = await API.get("/settings/countries");
        const list = Array.isArray(res.data?.countries) ? res.data.countries : [];
        const enabledList = list.filter((c) => c?.isEnabled !== false);
        const codes = enabledList
          .filter((c) => c?.isEnabled !== false)
          .map((c) => normalizeCountryCode(c?.countryCode))
          .filter(Boolean);
        const unique = Array.from(new Set(codes));
        const countryList = unique.length ? unique : [defaultCountry];
        const nameMap = unique.reduce((acc, code) => {
          const match = enabledList.find((c) => normalizeCountryCode(c?.countryCode) === code);
          acc[code] = match?.countryName || code;
          return acc;
        }, {});
        const currencyMap = unique.reduce((acc, code) => {
          const match = enabledList.find((c) => normalizeCountryCode(c?.countryCode) === code);
          acc[code] = buildCurrencyMeta(match);
          return acc;
        }, {});
        const nextCurrencyMap = unique.length
          ? currencyMap
          : { [defaultCountry]: { preferredCurrency: "USD", currencyOptions: ["USD"] } };

        if (!cancelled) {
          setEnabledCountries(countryList);
          setEnabledCountryNames(unique.length ? nameMap : { [defaultCountry]: defaultCountry });
          setCountryCurrencyMeta(nextCurrencyMap);
          setCountry((currentCountry) => {
            if (countryList.length && !countryList.includes(currentCountry)) {
              setPage(1);
              setCurrency(getPreferredCurrency(nextCurrencyMap[countryList[0]]));
              return countryList[0];
            }
            setCurrency(getPreferredCurrency(nextCurrencyMap[currentCountry]));
            return currentCountry;
          });
        }
      } catch (e) {
        if (!cancelled) {
          setEnabledCountries([defaultCountry]);
          setEnabledCountryNames({ [defaultCountry]: defaultCountry });
          setCountryCurrencyMeta({
            [defaultCountry]: { preferredCurrency: "USD", currencyOptions: ["USD"] },
          });
          setCountry(defaultCountry);
          setCurrency("USD");
        }
      }
    };

    loadCountries();
    return () => {
      cancelled = true;
    };
  }, [defaultCountry]);

  useEffect(() => {
    let cancelled = false;

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const runFetch = async (attempt = 0) => {
      const selectedCurrency = normalizeCurrencyCode(currency, "USD");
      const action = await dispatch(fetchProducts({ country, currency: selectedCurrency, page, limit, append: false, search }));

      if (!cancelled && action?.error && attempt < 1) {
        await delay(2000);
        if (!cancelled) return runFetch(attempt + 1);
      }
      return action;
    };

    const debounceMs = search ? 350 : 0;
    const t = setTimeout(() => {
      if (!cancelled) runFetch();
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [dispatch, country, currency, page, limit, search]);

  if (status === "loading") {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <div className="flex flex-col items-center gap-3 rounded-md border border-slate-200 bg-white px-8 py-7">
          <Loader2 className="h-7 w-7 animate-spin text-slate-900" />
          <p className="text-sm font-medium text-slate-600">Loading products...</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
        <div className="rounded-md border border-rose-200 bg-rose-50 p-5 text-rose-700">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.12em]">Failed to load products</h3>
              <p className="mt-1 text-sm font-medium">{error || 'An error occurred while loading products'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
      <ProductList
        products={products}
        pagination={pagination}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(1);
        }}
        country={country}
        currency={normalizeCurrencyCode(currency, "USD")}
        currencyOptions={countryCurrencyMeta?.[country]?.currencyOptions || [normalizeCurrencyCode(currency, "USD")]}
        search={search}
        onSearchChange={(next) => {
          setSearch(next);
          setPage(1);
        }}
        countries={enabledCountries}
        countryNames={enabledCountryNames}
        priceCountry={settingsCountry || "US"}
        onCountryChange={(next) => {
          const nextCountry = normalizeCountryCode(next, defaultCountry);
          setCountry(nextCountry);
          setCurrency(getPreferredCurrency(countryCurrencyMeta?.[nextCountry]));
          setPage(1);
        }}
        onCurrencyChange={(next) => {
          const nextCurrency = normalizeCurrencyCode(next, getPreferredCurrency(countryCurrencyMeta?.[country]));
          setCurrency(nextCurrency);
          setPage(1);
        }}
      />
    </div>
  );
}
